import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  Settings, RefreshCw, CreditCard, Clock, Activity, FileText, CheckCircle2, 
  ShieldCheck, Download, DollarSign, Layers, Zap, Globe, Lock, QrCode, 
  Smartphone, Wallet, Check, AlertTriangle, ArrowUpRight, ChevronRight, Key, Server
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

export function BillingAdminView() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'config' | 'subscriptions' | 'invoices' | 'methods'>('config');

  // Config State
  const [testPublishableKey, setTestPublishableKey] = useState('');
  const [testSecretKey, setTestSecretKey] = useState('');
  const [testWebhookSecret, setTestWebhookSecret] = useState('');
  const [livePublishableKey, setLivePublishableKey] = useState('');
  const [liveSecretKey, setLiveSecretKey] = useState('');
  const [liveWebhookSecret, setLiveWebhookSecret] = useState('');
  const [environment, setEnvironment] = useState('sandbox');

  // Payment Methods
  const [enableStripe, setEnableStripe] = useState(true);
  const [enablePix, setEnablePix] = useState(true);
  const [enableBoleto, setEnableBoleto] = useState(true);

  const { data: paymentMethods, refetch: refetchPaymentMethods } = useQuery({
    queryKey: ['admin-payment-methods'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/billing/payment-methods');
      const payload = res.data?.data ?? res.data;
      return payload;
    },
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (paymentMethods) {
      setEnableStripe(paymentMethods.stripe ?? true);
      setEnablePix(paymentMethods.pix ?? true);
      setEnableBoleto(paymentMethods.boleto ?? true);
    }
  }, [paymentMethods]);

  const savePaymentMethodsMutation = useMutation({
    mutationFn: (data: { stripe: boolean; pix: boolean; boleto: boolean }) =>
      apiClient.post('/api/admin/billing/payment-methods', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-methods'] });
      showSuccess('Métodos de pagamento atualizados!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || 'Erro ao salvar métodos de pagamento.');
    },
  });

  // Query Stripe Config
  const { data: config, isLoading: loadingConfig, refetch: refetchConfig } = useQuery({
    queryKey: ['admin-stripe-config'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/stripe/config');
      const data = res.data;
      if (data) {
        setTestPublishableKey(data.testPublishableKey || '');
        setTestSecretKey(data.testSecretKey || '');
        setTestWebhookSecret(data.testWebhookSecret || '');
        setLivePublishableKey(data.livePublishableKey || '');
        setLiveSecretKey(data.liveSecretKey || '');
        setLiveWebhookSecret(data.liveWebhookSecret || '');
        setEnvironment(data.environment || 'sandbox');
      }
      return data;
    },
  });

  // Query Invoices
  const { data: invoices = [], isLoading: loadingInvoices } = useQuery<any[]>({
    queryKey: ['admin-billing-invoices'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/billing/invoices');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeTab === 'invoices',
  });

  // Query Subscriptions
  const { data: subscriptions = [], isLoading: loadingSubs } = useQuery<any[]>({
    queryKey: ['admin-billing-subscriptions'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/billing/subscriptions');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeTab === 'subscriptions',
  });

  // Mutations
  const saveConfigMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/admin/stripe/config', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-stripe-config'] });
      showSuccess('Configurações do Stripe salvas com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao salvar credenciais.');
    },
  });

  const provisionWebhookMutation = useMutation({
    mutationFn: () => apiClient.post('/api/admin/stripe/provision-webhook'),
    onSuccess: (res) => {
      showSuccess(`Webhook provisionado com sucesso: ${res.data?.webhookUrl || ''}`);
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao provisionar webhook.');
    },
  });

  const handleSaveConfig = (e: React.FormEvent) => {
    e.preventDefault();
    saveConfigMutation.mutate({
      testPublishableKey,
      testSecretKey,
      testWebhookSecret,
      livePublishableKey,
      liveSecretKey,
      liveWebhookSecret,
      environment,
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <CreditCard size={14} /> Gateway & Faturamento Global
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Billing & Stripe Configuration
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Gerenciamento de credenciais de pagamento, webhooks e faturas recorrentes da plataforma.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'config'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Credenciais Stripe
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'subscriptions'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Assinaturas Ativas
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'invoices'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Faturas Emitidas
        </button>
        <button
          onClick={() => setActiveTab('methods')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'methods'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Métodos Aceitos
        </button>
      </div>

      {/* Tab: Config */}
      {activeTab === 'config' && (
        <form onSubmit={handleSaveConfig} className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Ambiente de Execução</h3>
              <p className="text-xs text-gray-500">Alterne entre modo de testes (sandbox) e produção.</p>
            </div>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800"
            >
              <option value="sandbox">Sandbox / Testes</option>
              <option value="live">Produção (Live)</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sandbox */}
            <div className="space-y-4 p-4 rounded-xl bg-gray-50/50 border border-gray-200/60">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Chaves de Teste (Sandbox)</h4>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Publishable Key</label>
                <input
                  type="text"
                  value={testPublishableKey}
                  onChange={(e) => setTestPublishableKey(e.target.value)}
                  placeholder="pk_test_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Secret Key</label>
                <input
                  type="password"
                  value={testSecretKey}
                  onChange={(e) => setTestSecretKey(e.target.value)}
                  placeholder="sk_test_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Webhook Secret</label>
                <input
                  type="password"
                  value={testWebhookSecret}
                  onChange={(e) => setTestWebhookSecret(e.target.value)}
                  placeholder="whsec_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>
            </div>

            {/* Live */}
            <div className="space-y-4 p-4 rounded-xl bg-gray-50/50 border border-gray-200/60">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Chaves de Produção (Live)</h4>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Publishable Key</label>
                <input
                  type="text"
                  value={livePublishableKey}
                  onChange={(e) => setLivePublishableKey(e.target.value)}
                  placeholder="pk_live_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Secret Key</label>
                <input
                  type="password"
                  value={liveSecretKey}
                  onChange={(e) => setLiveSecretKey(e.target.value)}
                  placeholder="sk_live_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Webhook Secret</label>
                <input
                  type="password"
                  value={liveWebhookSecret}
                  onChange={(e) => setLiveWebhookSecret(e.target.value)}
                  placeholder="whsec_..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => provisionWebhookMutation.mutate()}
              disabled={provisionWebhookMutation.isPending}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-xs font-semibold transition-all"
            >
              {provisionWebhookMutation.isPending ? 'Provisionando...' : 'Provisionar Webhook Endpoint'}
            </button>

            <button
              type="submit"
              disabled={saveConfigMutation.isPending}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-all disabled:opacity-50"
            >
              {saveConfigMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </form>
      )}

      {/* Tab: Subscriptions */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
          {loadingSubs ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-xs text-gray-500 mt-2">Carregando assinaturas...</p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
                <tr>
                  <th className="py-3 px-4">Código</th>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Plano</th>
                  <th className="py-3 px-4">Valor Mensal</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Renovação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50/50">
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500">{sub.id}</td>
                    <td className="py-3.5 px-4 font-semibold text-gray-900">{sub.organizationName}</td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex px-2 py-0.5 bg-indigo-50 text-indigo-700 font-bold rounded-full text-[10px]">
                        {sub.plan}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-gray-900">
                      R$ {Number(sub.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded-full text-[10px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Ativa
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(sub.currentPeriodEnd).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: Invoices */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
          {loadingInvoices ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-xs text-gray-500 mt-2">Carregando faturas...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-xs">Nenhuma fatura registrada.</div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
                <tr>
                  <th className="py-3 px-4">Fatura</th>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Vencimento</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50/50">
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500">{inv.invoiceNumber}</td>
                    <td className="py-3.5 px-4 font-semibold text-gray-900">{inv.organizationName}</td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(inv.dueDate).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        inv.status === 'PAID' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {inv.status === 'PAID' ? 'Paga' : 'Pendente'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-gray-900">
                      R$ {Number(inv.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: Methods */}
      {activeTab === 'methods' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-4">
          <h3 className="text-sm font-bold text-gray-900 mb-2">Formas de Pagamento Habilitadas no Checkout</h3>
          
          <label className="flex items-center justify-between p-4 rounded-xl bg-gray-50/75 border border-gray-200/60 cursor-pointer">
            <div className="flex items-center gap-3">
              <CreditCard size={20} className="text-indigo-600" />
              <div>
                <p className="text-xs font-bold text-gray-900">Cartão de Crédito (Stripe)</p>
                <p className="text-[11px] text-gray-500">Cobrança recorrente automatizada mensal e anual</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={enableStripe}
              onChange={(e) => {
                setEnableStripe(e.target.checked);
                savePaymentMethodsMutation.mutate({ stripe: e.target.checked, pix: enablePix, boleto: enableBoleto });
              }}
              disabled={savePaymentMethodsMutation.isPending}
              className="rounded-md border-gray-300 text-indigo-600 h-4 w-4"
            />
          </label>

          <label className="flex items-center justify-between p-4 rounded-xl bg-gray-50/75 border border-gray-200/60 cursor-pointer">
            <div className="flex items-center gap-3">
              <QrCode size={20} className="text-emerald-600" />
              <div>
                <p className="text-xs font-bold text-gray-900">PIX Instantâneo</p>
                <p className="text-[11px] text-gray-500">QR Code dinâmico com compensação imediata</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={enablePix}
              onChange={(e) => {
                setEnablePix(e.target.checked);
                savePaymentMethodsMutation.mutate({ stripe: enableStripe, pix: e.target.checked, boleto: enableBoleto });
              }}
              disabled={savePaymentMethodsMutation.isPending}
              className="rounded-md border-gray-300 text-indigo-600 h-4 w-4"
            />
          </label>

          <label className="flex items-center justify-between p-4 rounded-xl bg-gray-50/75 border border-gray-200/60 cursor-pointer">
            <div className="flex items-center gap-3">
              <FileText size={20} className="text-amber-600" />
              <div>
                <p className="text-xs font-bold text-gray-900">Boleto Bancário</p>
                <p className="text-[11px] text-gray-500">Emissão para compras corporativas com vencimento configurável</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={enableBoleto}
              onChange={(e) => {
                setEnableBoleto(e.target.checked);
                savePaymentMethodsMutation.mutate({ stripe: enableStripe, pix: enablePix, boleto: e.target.checked });
              }}
              disabled={savePaymentMethodsMutation.isPending}
              className="rounded-md border-gray-300 text-indigo-600 h-4 w-4"
            />
          </label>
        </div>
      )}
    </div>
  );
}
